import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';

import { ApiError } from '../api/client';
import { patchContact, type PatchContactBody } from '../api/contacts';
import { getDialog, handoff, listDialogs } from '../api/dialogs';
import type { DialogDto, DialogListFilters, Page } from '../api/types';
import { useToast } from '../components/ui/toast';
import { wsClient } from '../ws/client';

export function useDialogList(filters: DialogListFilters) {
  return useQuery({
    queryKey: ['dialogs', 'list', filters],
    queryFn: () => listDialogs(filters),
    refetchOnWindowFocus: false,
  });
}

export function useDialog(id: string | undefined) {
  return useQuery({
    queryKey: ['dialogs', 'detail', id],
    queryFn: () => getDialog(id as string),
    enabled: Boolean(id),
  });
}

/** Subscribes the WS connection to the channels currently visible in the inbox list. */
export function useSubscribeChannels(channelIds: string[]): void {
  // Depend on a stable string key so a new array with the same channels does not resubscribe.
  const key = channelIds.slice().sort().join(',');
  useEffect(() => {
    if (key) wsClient.subscribe(key.split(','));
  }, [key]);
}

export function useInvalidateDialogs() {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: ['dialogs'] });
  };
}

/** Patches every cached `dialogs/list` page in place, across all active filter variants. */
function patchDialogInLists(queryClient: ReturnType<typeof useQueryClient>, id: string, patch: (d: DialogDto) => DialogDto) {
  const snapshots = queryClient.getQueriesData<Page<DialogDto>>({ queryKey: ['dialogs', 'list'] });
  queryClient.setQueriesData<Page<DialogDto>>({ queryKey: ['dialogs', 'list'] }, (old) =>
    old ? { ...old, items: old.items.map((d) => (d.id === id ? patch(d) : d)) } : old,
  );
  return snapshots;
}

function rollback(queryClient: ReturnType<typeof useQueryClient>, snapshots: [readonly unknown[], unknown][]) {
  for (const [key, data] of snapshots) queryClient.setQueryData(key, data);
}

/** Row-level (§U1) funnel-stage change with optimistic update of every dialogs/list cache. */
export function useDialogStageMutation() {
  const queryClient = useQueryClient();
  const toast = useToast();
  return useMutation({
    mutationFn: ({ dialog, stage }: { dialog: DialogDto; stage: string }) =>
      patchContact(dialog.contact.id, { funnel_stage: stage }),
    onMutate: async ({ dialog, stage }) => {
      await queryClient.cancelQueries({ queryKey: ['dialogs', 'list'] });
      const snapshots = patchDialogInLists(queryClient, dialog.id, (d) => ({
        ...d,
        contact: { ...d.contact, funnelStage: stage },
      }));
      return { snapshots };
    },
    onError: (err, _vars, ctx) => {
      if (ctx) rollback(queryClient, ctx.snapshots);
      toast.push(err instanceof ApiError ? err.message : 'Не удалось изменить стадию');
    },
    onSuccess: (_res, { dialog }) => {
      void queryClient.invalidateQueries({ queryKey: ['contacts'] });
      void queryClient.invalidateQueries({ queryKey: ['dialogs', 'detail', dialog.id] });
    },
  });
}

/** Row-level "Взять в работу" / "Вернуть агенту", optimistic on the dialogs/list caches. */
export function useDialogHandoffMutation() {
  const queryClient = useQueryClient();
  const toast = useToast();
  return useMutation({
    mutationFn: ({ dialog, to, mode }: { dialog: DialogDto; to: 'human' | 'agent'; mode?: 'continue' | 'close' }) =>
      handoff(dialog.id, to, mode),
    onMutate: async ({ dialog, to, mode }) => {
      await queryClient.cancelQueries({ queryKey: ['dialogs', 'list'] });
      const nextState: DialogDto['state'] = to === 'human' ? 'HUMAN_ACTIVE' : mode === 'close' ? 'CLOSED' : 'AGENT_ACTIVE';
      const snapshots = patchDialogInLists(queryClient, dialog.id, (d) => ({ ...d, state: nextState }));
      return { snapshots };
    },
    onError: (err, _vars, ctx) => {
      if (ctx) rollback(queryClient, ctx.snapshots);
      toast.push(err instanceof ApiError ? err.message : 'Действие не выполнено');
    },
    onSuccess: (updated) => {
      queryClient.setQueryData(['dialogs', 'detail', updated.id], updated);
    },
  });
}

/** Row-level tag edit (add/remove), optimistic on the dialogs/list caches. */
export function useDialogTagsMutation() {
  const queryClient = useQueryClient();
  const toast = useToast();
  return useMutation({
    mutationFn: ({ dialog, tags }: { dialog: DialogDto; tags: string[] }) => {
      const body: PatchContactBody = { tags };
      return patchContact(dialog.contact.id, body);
    },
    onMutate: async ({ dialog, tags }) => {
      await queryClient.cancelQueries({ queryKey: ['dialogs', 'list'] });
      const snapshots = patchDialogInLists(queryClient, dialog.id, (d) => ({
        ...d,
        contact: { ...d.contact, tags },
      }));
      return { snapshots };
    },
    onError: (err, _vars, ctx) => {
      if (ctx) rollback(queryClient, ctx.snapshots);
      toast.push(err instanceof ApiError ? err.message : 'Не удалось обновить теги');
    },
    onSuccess: (_res, { dialog }) => {
      void queryClient.invalidateQueries({ queryKey: ['contacts', 'detail', dialog.contact.id] });
    },
  });
}
